import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Search, Shield, Ban, KeyRound, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { aiQuotaMonthKey } from "@/hooks/useAiQuota";
import { useQueryClient } from "@tanstack/react-query";

interface UserProfile {
  id: string;
  user_id: string;
  email?: string;
  nickname: string | null;
  avatar_url: string | null;
  preferred_level: string | null;
  created_at: string;
  roles: string[];
  cdk_code?: string;
  is_banned?: boolean;
}

interface QuotaRow {
  user_id: string;
  month: string;
  lookup_limit: number;
  lookup_remaining: number;
  analyze_limit: number;
  analyze_remaining: number;
}

const ADMIN_UNLIMITED = 2147483647;

export default function UserManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const monthKey = aiQuotaMonthKey();

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [quotaMap, setQuotaMap] = useState<Record<string, QuotaRow>>({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<"user" | "cdk">("user");

  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const [quotaUser, setQuotaUser] = useState<UserProfile | null>(null);
  const [lookupLimitInput, setLookupLimitInput] = useState("");
  const [lookupRemainingInput, setLookupRemainingInput] = useState("");
  const [savingQuota, setSavingQuota] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await (supabase as any).rpc("get_users_with_email");
      if (error) throw error;

      const mappedUsers: UserProfile[] = (data || []).map((u: any) => ({
        id: u.id,
        user_id: u.user_id,
        email: u.email,
        nickname: u.nickname,
        avatar_url: u.avatar_url,
        preferred_level: u.preferred_level,
        created_at: u.created_at,
        roles: u.roles || [],
        cdk_code: u.cdk_code,
        is_banned: u.is_banned,
      }));
      setUsers(mappedUsers);

      const { data: quotaData, error: quotaError } = await (supabase as any).rpc("admin_get_ai_quota_monthly", {
        _month: monthKey,
      });
      if (quotaError) throw quotaError;

      const nextQuotaMap: Record<string, QuotaRow> = {};
      for (const row of (quotaData || []) as QuotaRow[]) {
        nextQuotaMap[row.user_id] = row;
      }
      setQuotaMap(nextQuotaMap);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({ title: "获取用户数据失败", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchUsers();
  }, []);

  const handleToggleRole = async (userId: string, role: "premium" | "admin", hasRole: boolean) => {
    try {
      if (hasRole) {
        const { error } = await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", role);
        if (error) throw error;
        toast({ title: `已移除 ${role} 角色` });
      } else {
        const { error } = await supabase.from("user_roles").insert({ user_id: userId, role });
        if (error) throw error;
        toast({ title: `已添加 ${role} 角色` });
      }
      await fetchUsers();
    } catch (error) {
      console.error("Error toggling role:", error);
      toast({ title: "操作失败", variant: "destructive" });
    }
  };

  const handleBanUser = async (userId: string, cdkCode?: string) => {
    try {
      await supabase.from("user_roles").insert({ user_id: userId, role: "banned" as any });

      if (cdkCode) {
        await supabase.from("cdk_codes").update({ description: "已封禁用户" }).eq("code", cdkCode);
      }

      toast({ title: "用户已封禁", description: cdkCode ? "关联卡密已标记" : undefined });
      await fetchUsers();
    } catch (error) {
      console.error("Error banning user:", error);
      toast({ title: "封禁失败", variant: "destructive" });
    }
  };

  const handleUnbanUser = async (userId: string) => {
    try {
      await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", "banned" as any);
      toast({ title: "已解除封禁" });
      await fetchUsers();
    } catch (error) {
      console.error("Error unbanning user:", error);
      toast({ title: "操作失败", variant: "destructive" });
    }
  };

  const getUserLookupQuota = (user: UserProfile) => {
    const isAdmin = user.roles.includes("admin");
    if (isAdmin) {
      return {
        limit: ADMIN_UNLIMITED,
        remaining: ADMIN_UNLIMITED,
        unlimited: true,
      };
    }

    const row = quotaMap[user.user_id];
    if (row) {
      return {
        limit: Number(row.lookup_limit) || 0,
        remaining: Number(row.lookup_remaining) || 0,
        unlimited: false,
      };
    }

    const defaultLimit = user.roles.includes("premium") ? 300 : 50;
    return {
      limit: defaultLimit,
      remaining: defaultLimit,
      unlimited: false,
    };
  };

  const openQuotaEditor = (user: UserProfile) => {
    if (user.roles.includes("admin")) {
      toast({ title: "管理员账号为无限额度，无需设置" });
      return;
    }

    const quota = getUserLookupQuota(user);
    setQuotaUser(user);
    setLookupLimitInput(String(quota.limit));
    setLookupRemainingInput(String(quota.remaining));
    setQuotaDialogOpen(true);
  };

  const saveLookupQuota = async () => {
    if (!quotaUser) return;

    const lookupLimit = Number.parseInt(lookupLimitInput, 10);
    const lookupRemaining = Number.parseInt(lookupRemainingInput, 10);

    if (!Number.isFinite(lookupLimit) || lookupLimit < 0) {
      toast({ title: "查词总额度必须是大于等于 0 的整数", variant: "destructive" });
      return;
    }
    if (!Number.isFinite(lookupRemaining) || lookupRemaining < 0) {
      toast({ title: "查词剩余额度必须是大于等于 0 的整数", variant: "destructive" });
      return;
    }
    if (lookupRemaining > lookupLimit) {
      toast({ title: "查词剩余额度不能大于总额度", variant: "destructive" });
      return;
    }

    setSavingQuota(true);
    try {
      const { error } = await (supabase as any).rpc("admin_set_ai_lookup_quota", {
        _user_id: quotaUser.user_id,
        _lookup_limit: lookupLimit,
        _lookup_remaining: lookupRemaining,
        _month: monthKey,
      });
      if (error) throw error;

      toast({ title: "AI 查词额度已更新" });
      await fetchUsers();
      await queryClient.invalidateQueries({ queryKey: ["ai-quota", quotaUser.user_id] });
      setQuotaDialogOpen(false);
      setQuotaUser(null);
    } catch (error) {
      console.error("Failed to update quota:", error);
      toast({ title: "更新额度失败", variant: "destructive" });
    } finally {
      setSavingQuota(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;

    return users.filter((user) => {
      if (searchType === "cdk") {
        return user.cdk_code?.toLowerCase().includes(query);
      }
      return (
        user.nickname?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.user_id.toLowerCase().includes(query)
      );
    });
  }, [searchQuery, searchType, users]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const roleColors: Record<string, string> = {
    admin: "bg-rose-500/15 text-rose-600",
    premium: "bg-amber-500/15 text-amber-600",
    moderator: "bg-purple-500/15 text-purple-600",
    user: "bg-slate-500/15 text-slate-600",
    banned: "bg-red-500/15 text-red-600",
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">用户管理</h1>
          <p className="text-muted-foreground">查看和管理用户，支持卡密搜索与 AI 查词额度调整</p>
        </div>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Select value={searchType} onValueChange={(v: "user" | "cdk") => setSearchType(v)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">搜用户</SelectItem>
                <SelectItem value="cdk">搜卡密</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchType === "cdk" ? "输入卡密搜索..." : "搜索昵称、邮箱或用户ID..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Button variant="outline" size="icon" onClick={() => void fetchUsers()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>用户列表</span>
            <span className="text-sm font-normal text-muted-foreground">共 {filteredUsers.length} 人</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>用户</TableHead>
                  <TableHead>邮箱</TableHead>
                  <TableHead>卡密</TableHead>
                  <TableHead>角色</TableHead>
                  <TableHead>AI查词额度</TableHead>
                  <TableHead>注册时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {searchQuery ? "未找到匹配用户" : "暂无用户"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => {
                    const quota = getUserLookupQuota(user);
                    return (
                      <TableRow key={user.id} className={user.is_banned ? "opacity-60" : ""}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarImage src={user.avatar_url || undefined} />
                              <AvatarFallback className="bg-primary/10 text-primary">
                                {user.nickname?.charAt(0).toUpperCase() || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                {user.nickname || "未设置昵称"}
                                {user.is_banned && (
                                  <Badge variant="destructive" className="text-xs">已封禁</Badge>
                                )}
                              </p>
                              <p className="text-xs text-muted-foreground font-mono">{user.user_id.substring(0, 8)}...</p>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <span className="text-sm text-muted-foreground font-mono">{user.email || "-"}</span>
                        </TableCell>

                        <TableCell>
                          {user.cdk_code ? (
                            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{user.cdk_code}</code>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            {user.roles.filter((r) => r !== "banned").length === 0 ? (
                              <Badge className={roleColors.user + " border-0"}>user</Badge>
                            ) : (
                              user.roles
                                .filter((r) => r !== "banned")
                                .map((role) => (
                                  <Badge key={role} className={(roleColors[role] || roleColors.user) + " border-0"}>
                                    {role}
                                  </Badge>
                                ))
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          {quota.unlimited ? (
                            <Badge className="bg-emerald-500/15 text-emerald-600 border-0">∞ 无限</Badge>
                          ) : (
                            <span className="font-mono text-sm">{quota.remaining}/{quota.limit}</span>
                          )}
                        </TableCell>

                        <TableCell className="text-muted-foreground text-sm">{formatDate(user.created_at)}</TableCell>

                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className={user.roles.includes("premium") ? "text-amber-500" : ""}
                              onClick={() =>
                                void handleToggleRole(user.user_id, "premium", user.roles.includes("premium"))
                              }
                            >
                              <Shield className="h-4 w-4 mr-1" />
                              会员
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openQuotaEditor(user)}
                              disabled={user.roles.includes("admin")}
                            >
                              <SlidersHorizontal className="h-4 w-4 mr-1" />
                              额度
                            </Button>

                            {user.is_banned ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600"
                                onClick={() => void handleUnbanUser(user.user_id)}
                              >
                                <KeyRound className="h-4 w-4 mr-1" />
                                解封
                              </Button>
                            ) : (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                                    <Ban className="h-4 w-4 mr-1" />
                                    封禁
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>确认封禁用户？</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      封禁后用户将无法登录和使用。
                                      {user.cdk_code && (
                                        <span className="block mt-2 text-amber-600">
                                          关联卡密 {user.cdk_code} 也将被标记。
                                        </span>
                                      )}
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>取消</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => void handleBanUser(user.user_id, user.cdk_code)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      确认封禁
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={quotaDialogOpen} onOpenChange={setQuotaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>调整 AI 查词额度</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-muted-foreground">
              用户：{quotaUser?.nickname || quotaUser?.email || quotaUser?.user_id}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="lookup-limit">查词总额度（本月）</Label>
              <Input
                id="lookup-limit"
                inputMode="numeric"
                value={lookupLimitInput}
                onChange={(e) => setLookupLimitInput(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="例如 300"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="lookup-remaining">查词剩余额度（本月）</Label>
              <Input
                id="lookup-remaining"
                inputMode="numeric"
                value={lookupRemainingInput}
                onChange={(e) => setLookupRemainingInput(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="例如 128"
              />
            </div>
            <p className="text-xs text-muted-foreground">仅调整本月查词额度，句子解析额度保持原值不变。</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuotaDialogOpen(false)} disabled={savingQuota}>
              取消
            </Button>
            <Button onClick={() => void saveLookupQuota()} disabled={savingQuota || !quotaUser}>
              {savingQuota ? "保存中..." : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
